/**
 * Peer-Reviewed Knowledge Base — curated findings from nuclear, data center,
 * and BTM power research that ground AI report generation.
 *
 * Each entry has: topic, findings (key facts), source citation, and tags
 * for matching against report sections.
 *
 * This database is used as context for Gemini when generating BTM options,
 * connection risks, development risks, and recommendations.
 */

export interface KnowledgeEntry {
  id: string;
  topic: string;
  findings: string;
  source: string;       // Full citation
  year: number;
  tags: string[];        // For matching: 'btm', 'smr', 'cooling', 'grid', 'seismic', etc.
  /** Cross-technology comparisons remain useful when one technology is excluded. */
  applicability?: 'cross-technology';
  /** Optional reactor design classes this entry applies to (v1 generic classes). */
  reactorClasses?: import('../../config/reactorDesignCatalog').ReactorDesignClassId[];
  /**
   * Provenance of the entry within a DAM build bundle (EC5).
   * - `static`   — came from the in-source `PEER_REVIEW_DB` constant
   * - `curated`  — came from `curated_kb_entries` (admin-approved via EC1)
   * Optional because the underlying static array doesn't carry the field;
   * the DAM build pipeline stamps it onto every entry in its merged pool.
   */
  librarySource?: 'static' | 'curated' | 'curator-queue';
}

/**
 * Curated peer-reviewed findings for BTM feasibility assessment.
 * Sources: DOE, NRC, EPRI, IEEE, ASHRAE, ANS, IAEA publications.
 */
export const PEER_REVIEW_DB: KnowledgeEntry[] = [
  // SMR Technology
  {
    id: 'smr-001',
    topic: 'SMR Power Density and Land Use',
    findings: 'Small Modular Reactors (10-300 MWe) require 0.5-2 acres per 100 MW, compared to 5-10 acres for solar+BESS at equivalent firm capacity. NuScale VOYGR-6 (462 MWe) requires ~35 acres including exclusion zone. Emergency Planning Zone reduced to site boundary for NRC-approved designs.',
    source: 'DOE-NE, "Advanced Small Modular Reactors (SMRs) Fact Sheet", Office of Nuclear Energy, 2023.',
    year: 2023,
    tags: ['smr', 'btm', 'land-use', 'power-density'],
  },
  {
    id: 'smr-002',
    topic: 'SMR Licensing Timeline',
    findings: 'NRC Part 50/52 licensing for SMRs takes 3-7 years from application to operating license. Combined License (COL) applications require site-specific Environmental Impact Statement. Early Site Permits (ESP) can be obtained independently, reducing construction-phase risk by 2+ years.',
    source: 'NRC, "Licensing Process for New Reactors", NUREG/BR-0468, Rev. 2, 2022.',
    year: 2022,
    tags: ['smr', 'regulatory', 'licensing', 'timeline', 'foundational'],
  },
  {
    id: 'smr-003',
    topic: 'SMR Construction Cost Estimates',
    findings: 'First-of-a-kind SMR costs estimated at $6,000-$10,000/kW. Nth-of-a-kind projections (after 5+ units): $3,500-$5,500/kW. Factory fabrication and modular assembly reduce on-site construction time to 24-36 months vs. 60-84 months for large reactors.',
    source: 'EPRI, "Advanced Nuclear Technology: Small Modular Reactor Cost Study", Report 3002018030, 2023.',
    year: 2023,
    tags: ['smr', 'cost', 'construction'],
  },
  // Data Center Power & Cooling
  {
    id: 'dc-001',
    topic: 'Data Center Power Requirements',
    findings: 'Hyperscale AI data centers consume 50-200+ MW. AI/ML training clusters require 40-80 kW per rack (vs. 8-15 kW for general compute). Power Usage Effectiveness (PUE) targets: 1.10-1.20 for air-cooled, 1.03-1.08 for liquid-cooled facilities.',
    source: 'Uptime Institute, "Global Data Center Survey Results 2024", 2024.',
    year: 2024,
    tags: ['datacenter', 'power', 'pue', 'cooling', 'general'],
  },
  {
    id: 'dc-002',
    topic: 'Cooling Technology Selection by Climate',
    findings: 'Adiabatic/evaporative cooling: optimal when wet bulb < 18°C for >70% of hours. Direct liquid cooling (DLC): reduces cooling energy by 30-40% vs. air cooling. Closed-loop dry cooling: required in water-stressed regions (WRI Aqueduct score > 3.0). Absorption chillers utilizing waste heat achieve COP 0.7-1.2.',
    source: 'ASHRAE, "Thermal Guidelines for Data Processing Environments, 5th Ed.", TC 9.9, 2021.',
    year: 2021,
    tags: ['cooling', 'datacenter', 'climate', 'water-stress', 'general'],
  },
  {
    id: 'dc-003',
    topic: 'Data Center Water Consumption',
    findings: 'Evaporative cooling: 1.8-2.5 L/kWh IT load. Air-cooled (no evaporation): 0 L/kWh but 15-30% higher energy cost. Hybrid systems reduce water use by 60-80% vs. full evaporative while maintaining PUE < 1.15.',
    source: 'Shehabi, A. et al., "United States Data Center Energy Usage Report", LBNL-1005775, Lawrence Berkeley National Laboratory, 2016.',
    year: 2016,
    tags: ['cooling', 'water', 'datacenter'],
  },
  {
    id: 'dc-006',
    topic: 'AI Rack Density and Cooling Thresholds (2025)',
    findings: 'Current-generation AI training racks draw 40–140 kW; NVIDIA GB200 NVL72 is ≈120 kW nominal and ≈132 kW as deployed (115 kW liquid + 17 kW air), with frontier systems trending toward ~1 MW/rack. Air cooling reaches its practical limit near 40–50 kW/rack; direct-to-chip liquid cooling removes 70–75% of rack heat at a partial-PUE of 1.02–1.03, and single-phase immersion supports ~200 kW/rack. AI inference typically runs 10–30 kW/rack. Global average PUE remains ≈1.56, with hyperscale AI facilities approaching ~1.2.',
    source: 'Uptime Institute, "Global Data Center Survey 2024"; NVIDIA/HPE "GB200 NVL72" datasheet, 2025; Introl, "Liquid Cooling vs Air: the 50 kW GPU Rack Guide", 2025.',
    year: 2025,
    tags: ['datacenter', 'power', 'pue', 'cooling', 'general', 'btm'],
  },
  {
    id: 'dc-007',
    topic: 'AI Training Power Volatility and Storage Firming',
    findings: 'Large AI training jobs synchronize tens of thousands of GPUs into alternating compute- and communication-heavy phases, producing high-frequency, abrupt, synchronized power swings that can trigger transient voltage and frequency instability at gigawatt scale. Battery energy storage (plus supercapacitors near the racks) acts as an active power-architecture layer that decouples the volatile GPU load from the grid or behind-the-meter source, and the same storage firms intermittent renewables. Sustained AI training therefore requires storage firming regardless of the generation source chosen.',
    source: 'IEEE Spectrum, "Neutralizing the Gigascale Problem"; SemiAnalysis, "AI Training Load Fluctuations at Gigawatt-scale", 2025.',
    year: 2025,
    tags: ['datacenter', 'power', 'grid', 'btm', 'general'],
  },
  {
    id: 'dc-008',
    topic: 'US Data Center Electricity Growth',
    findings: 'US data centers consumed 176 TWh in 2023, about 4.4% of total US electricity, after demand more than doubled between 2017 and 2023 largely due to AI servers. Consumption is projected to reach 325–580 TWh (6.7–12% of US electricity) by 2028.',
    source: 'Shehabi, A. et al., "2024 United States Data Center Energy Usage Report", Lawrence Berkeley National Laboratory (DOE), 2024.',
    year: 2024,
    tags: ['datacenter', 'power', 'general'],
  },
  // BTM power-source ↔ workload fit
  {
    id: 'btm-001',
    applicability: 'cross-technology',
    topic: 'BTM Power-Source Fit by Workload (Capacity Factor)',
    findings: 'Load factor and criticality set the behind-the-meter power source a workload can use. Firm dispatchable sources suit high-load-factor AI training and colocation SLAs: natural-gas combined cycle at ≈60% capacity factor deploys fastest, large nuclear runs at ≈91%, and SMRs offer 95%+ but are generally post-2030 (LCOE ≈$89–102/MWh). Intermittent solar (≈23% capacity factor) and wind (≈34%), at LCOE ≈$26–50/MWh, require heavy overbuild plus BESS firming to serve sustained AI load and are better matched to flexible, batch, or inference workloads. Committing to an unfirmed intermittent source caps achievable load factor and steers a site away from training.',
    source: 'US EIA, "Electric Power Monthly, Tables 6.07.A (Fossil Fuels) & 6.07.B (Non-Fossil Fuels) — Capacity Factors", 2024; Introl/NuScale, "SMR Nuclear Power for AI Data Centers", 2025; Enverus, "Natural Gas Behind-the-Meter Power for Data Centers", 2025.',
    year: 2024,
    tags: ['btm', 'power', 'grid', 'datacenter', 'smr', 'solar', 'general'],
  },
  // Grid Interconnection
  {
    id: 'grid-001',
    topic: 'Generator Queue Benchmarks Versus Large-Load Connection',
    findings: 'Berkeley Lab Queued Up studies transmission-connected generation and storage projects. Its 2024 edition reports a median of five years from request to commercial operation for projects built in 2023; this includes development after studies and is not a data-center load-connection timetable. A regional generator-queue statistic does not establish a site queue position, utility capacity commitment, network-upgrade cost or energization date. MISO distinguishes new load registration from generator interconnection and is developing coordinated large-load and co-location pathways. The applicable utility and transmission-owner studies depend on the actual import, export and operating configuration. A non-export BTM design does not by itself establish exemption from interconnection review.',
    source: 'Berkeley Lab, Queued Up: 2024 Edition, https://eta.lbl.gov/publications/queued-2024-edition-characteristics; MISO, Large Load Additions, https://www.misoenergy.org/planning/large-loads---container-page/large-load-additions/; MISO, Market Participation and Registration of Co-Located Load and Generation, https://www.misoenergy.org/engage/MISO-Dashboard/market-participation-and-registration-of-co-located-load-and-generation-behind-the-same-poi/ (reviewed 2026-09-07).',
    year: 2026,
    tags: ['grid', 'interconnection', 'btm', 'timeline', 'general'],
  },
  {
    id: 'grid-002',
    topic: 'BTM Operating Configuration and Interconnection Requirements',
    findings: 'Grid-connected BTM, non-export parallel operation and an electrically isolated microgrid have different operating and interconnection requirements. On-site generation alone does not provide intentional islanding: the system must be designed to disconnect and supply the intended loads. MISO states that zero-injection generation still requires planning review; co-located load and generation must be evaluated for their actual point of interconnection and operating behavior. BTM therefore does not automatically eliminate utility studies, transmission or standby charges, or network upgrades. Tariff savings, service rights and an achievable delivery date remain project-specific questions for the serving utility and the applicable agreements.',
    source: 'US DOE, Solar Integration: Distributed Energy Resources and Microgrids Basics, https://www.energy.gov/cmei/systems/solar-integration-distributed-energy-resources-and-microgrids-basics; MISO, Response to Large Load Workshop Considerations and Outcomes, https://cdn.misoenergy.org/MISO%20Response%20to%20Large%20Load%20Workshop%20Considerations%20and%20Outcomes%20of%20Large%20Load%20Additions746069.pdf (reviewed 2026-09-07).',
    year: 2026,
    tags: ['btm', 'grid', 'cost', 'power'],
  },
  // Seismic Design
  {
    id: 'seis-001',
    topic: 'Seismic Design Requirements for Nuclear Facilities',
    findings: 'NRC requires Safe Shutdown Earthquake (SSE) design for all nuclear structures. SDC D or higher requires site-specific Probabilistic Seismic Hazard Analysis (PSHA). Foundation design: base isolation can reduce seismic loads by 50-80%. SMR designs inherently benefit from smaller, stiffer structures.',
    source: 'NRC, "Regulatory Guide 1.208: Performance-Based Approach to Define the Site-Specific Earthquake Ground Motion", 2007 (Rev. 2018).',
    year: 2018,
    tags: ['seismic', 'nuclear', 'smr', 'regulatory', 'foundational'],
  },
  // Environmental Permitting
  {
    id: 'env-001',
    topic: 'Environmental Impact Assessment for Nuclear Sites',
    findings: 'NRC NEPA review requires Environmental Impact Statement (EIS) or Environmental Assessment (EA). Timeline: 18-30 months for EIS. Critical habitat (ESA Section 7) consultation: 3-6 months if formal. Wetland fill (Section 404): requires USACE permit, 6-12 months. State-level permits add 6-18 months.',
    source: 'NRC, "Environmental Review Guidance for Licensing Actions", NUREG-1555, Rev. 1, 2007.',
    year: 2007,
    tags: ['environmental', 'regulatory', 'nuclear', 'permitting', 'foundational'],
  },
  // Renewable Integration
  {
    id: 'ren-001',
    topic: 'Solar + BESS for Data Center BTM',
    findings: 'Solar PV capacity factor: 15-28% depending on location (GHI 4-6 kWh/m²/day). 100 MW firm capacity requires ~400-500 MW solar + 400-800 MWh BESS (4-8 hour duration). Land requirement: 5-7 acres/MW for utility-scale solar. Not viable as sole BTM source for >50 MW DC loads due to land constraints.',
    source: 'NREL, "Solar Futures Study", DOE/EE-2491, 2021.',
    year: 2021,
    tags: ['solar', 'bess', 'btm', 'renewable', 'land-use'],
  },
  {
    id: 'ren-002',
    topic: 'Wind Resource for Data Center BTM',
    findings: 'Wind capacity factors: 25-45% at good sites (>7 m/s annual average at 100m). Single 3-5 MW turbine: 0.5-1 acre footprint but ~60 acre exclusion zone. Complementary to solar (wind peaks in winter/night). Not typically viable as sole BTM source due to intermittency.',
    source: 'DOE Wind Energy Technologies Office, "Land-Based Wind Market Report: 2024 Edition", 2024.',
    year: 2024,
    tags: ['wind', 'btm', 'renewable', 'land-use'],
  },
  // Natural Gas Bridge
  {
    id: 'gas-001',
    topic: 'Natural Gas as BTM Bridge Power',
    findings: 'Gas turbine simple cycle: 35-42% efficiency, $600-$1,000/kW installed. Combined cycle: 55-63% efficiency, $1,000-$1,500/kW. Permitting: 12-24 months typical. Air quality constraints in non-attainment areas may require offsets. CO2 emissions: 350-500 kg/MWh. Viable as 5-10 year bridge to SMR.',
    source: 'EIA, "Capital Cost and Performance Characteristic Estimates for Utility Scale Electric Power Generating Technologies", 2023.',
    year: 2023,
    tags: ['gas', 'btm', 'bridge', 'power', 'emissions'],
  },

  // Water & Cooling Systems
  {
    id: 'water-001',
    applicability: 'cross-technology',
    topic: 'Water Availability for Power Plant Cooling',
    findings: 'Once-through cooling: 25,000-60,000 gallons/MWh. Wet recirculating (cooling tower): 500-800 gallons/MWh. Dry cooling: 0 gallons/MWh but 2-5% efficiency penalty and 5x capital cost vs wet cooling. Hybrid wet/dry systems reduce water use by 50-80%. WRI Aqueduct water stress >3.0 mandates closed-loop.',
    source: 'Macknick, J. et al., "Operational water consumption and withdrawal factors for electricity generating technologies", NREL/TP-6A20-50900, 2012.',
    year: 2012,
    tags: ['water', 'cooling', 'datacenter', 'smr'],
  },
  {
    id: 'water-002',
    topic: 'SMR Water Requirements',
    findings: 'SMR designs with passive cooling (e.g., NuScale) can operate with significantly reduced water requirements compared to large LWRs. Air-cooled condenser configurations eliminate circulating water needs entirely at ~5-7% thermal efficiency penalty. Critical for arid/water-stressed sites.',
    source: 'EPRI, "Water Use for Electric Power Generation", Report 1023676, 2011.',
    year: 2011,
    tags: ['smr', 'water', 'cooling', 'nuclear'],
  },

  // Nuclear Safety & Design
  {
    id: 'nuc-001',
    topic: 'Nuclear Safety and Emergency Planning Zones',
    findings: 'Traditional EPZ: 10-mile plume exposure, 50-mile ingestion pathway. NRC-approved SMR designs (NuScale VOYGR): EPZ reduced to site boundary based on source term analysis. Eliminates off-site emergency planning requirements. Enables nuclear siting near population centers and data center campuses.',
    source: 'NRC, "Emergency Planning for Small Modular Reactors and Other New Technologies", SECY-18-0103, 2018.',
    year: 2018,
    tags: ['nuclear', 'smr', 'regulatory', 'safety', 'foundational'],
  },
  {
    id: 'nuc-002',
    topic: 'Nuclear Plant Decommissioning Costs',
    findings: 'Large reactor decommissioning: $500M-$1B over 10-60 years. SMR decommissioning estimated at $50M-$200M due to smaller structures and lower inventory. Decommissioning fund requirement: 2-3% of construction cost set aside annually. NRC Rule 10 CFR 50.75 governs funding.',
    source: 'NRC, "Decommissioning Nuclear Power Plants", NUREG/BR-0184, Rev. 2, 2021.',
    year: 2021,
    tags: ['nuclear', 'smr', 'cost', 'regulatory', 'decommissioning'],
  },
  {
    id: 'nuc-003',
    topic: 'SMR Fuel Supply and Enrichment',
    findings: 'Most SMR designs use HALEU (High-Assay Low-Enriched Uranium, 5-20% enrichment). Current US HALEU supply limited. DOE HALEU Availability Program targets 25 MT/year by 2030. Supply chain risk for first-mover SMR deployments. Some designs (e.g., Terrestrial Energy IMSR) use standard LEU (<5%).',
    source: 'DOE, "HALEU Availability Program Strategy", Office of Nuclear Energy, 2024.',
    year: 2024,
    tags: ['nuclear', 'smr', 'fuel', 'supply-chain', 'haleu'],
    reactorClasses: ['htgr', 'microreactor'],
  },
  {
    id: 'nuc-004',
    topic: 'SMR Reactor Types and Fuel Requirements',
    findings: 'HTGR/TRISO designs (X-energy Xe-100, Ultra Safe Nuclear MMR) require HALEU (5–20% enrichment). LWR-based SMRs (NuScale, Westinghouse AP300) and molten-salt designs (Terrestrial Energy IMSR) typically use standard LEU (<5%). Fuel choice is reactor-design-specific — HALEU supply chain risk applies only when HTGR or other high-assay designs are selected.',
    source: 'DOE HALEU Availability Program; NRC SMR design certifications; vendor fuel specifications, 2024.',
    year: 2024,
    tags: ['nuclear', 'smr', 'fuel', 'haleu', 'htgr', 'supply-chain'],
    reactorClasses: ['lwr-smr', 'htgr', 'molten-salt', 'microreactor', 'large-lwr'],
  },

  // Grid & Interconnection
  {
    id: 'grid-003',
    topic: 'Electricity Transmission Planning (Order No. 1920) & Generator Interconnection Queue Reform (Order No. 2023)',
    findings: 'US transmission system operating at 90%+ capacity in many regions. FERC Order No. 2023 (July 2023) and Order No. 2023-A (March 2024) reform generator interconnection queues (cluster studies, readiness deposits, and withdrawal penalties for on-site/co-located generation seeking grid interconnection), whereas FERC Order No. 1920 (May 2024) separately governs 20-year regional transmission planning and cost allocation; retail large-load data center service requests instead proceed under utility and RTO large-load study and tariff processes. Average cost of network transmission upgrades for >100 MW: $20-50M.',
    source: 'FERC Order No. 2023, "Improvements to Generator Interconnection Procedures and Agreements" (July 2023) & FERC Order No. 1920, "Building for the Future Through Electric Regional Transmission Planning and Cost Allocation" (May 2024).',
    year: 2024,
    tags: ['grid', 'datacenter', 'interconnection', 'cost'],
  },
  {
    id: 'grid-004',
    topic: 'Microgrid Design for Data Center Resilience',
    findings: 'Island-capable microgrids provide 99.9999% availability (six nines) when combining 2+ generation sources + BESS. Key components: automatic transfer switch (ATS), synchronizing switchgear, black-start capability. SMR + BESS microgrid eliminates grid dependency entirely. IEEE 1547 governs DER interconnection.',
    source: 'IEEE, "Standard for Interconnection and Interoperability of Distributed Energy Resources", IEEE Std 1547-2018, 2018.',
    year: 2018,
    tags: ['grid', 'microgrid', 'datacenter', 'btm', 'resilience'],
  },

  // Data Center Design
  {
    id: 'dc-004',
    topic: 'Data Center Tier Classification and Uptime',
    findings: 'Tier I: 99.671% uptime (28.8h/yr downtime). Tier II: 99.741% (22h/yr). Tier III: 99.982% (1.6h/yr). Tier IV: 99.995% (0.4h/yr). AI training workloads typically require Tier III+. BTM nuclear provides continuous baseload supporting Tier IV without utility grid dependency.',
    source: 'Uptime Institute, "Tier Standard: Topology", 2022.',
    year: 2022,
    tags: ['datacenter', 'reliability', 'btm'],
  },
  {
    id: 'dc-005',
    topic: 'Direct Liquid Cooling Economics',
    findings: 'DLC reduces cooling energy by 30-40% vs air cooling. Capital cost: $200-400/kW IT additional vs air-cooled baseline. Payback: 2-4 years at >30 kW/rack. Enables rack densities of 80-120 kW. Critical for GPU/TPU clusters (NVIDIA H100/B200 TDP 700W-1000W per chip).',
    source: 'ASHRAE, "Liquid Cooling Guidelines for Datacom Equipment Centers", 2014 (updated 2023).',
    year: 2023,
    tags: ['cooling', 'datacenter', 'cost', 'liquid-cooling'],
  },

  // Environmental & Permitting
  {
    id: 'env-002',
    topic: 'Wetlands Mitigation Banking',
    findings: 'Section 404 Clean Water Act: wetland fill requires USACE permit. Mitigation ratio: 1:1 to 3:1 (acres mitigated per acre impacted). Mitigation bank credits: $20,000-$200,000 per acre depending on region. In-lieu fee programs available in most states. Timeline: 6-18 months.',
    source: 'USACE, "Regulatory Program Overview", EP 1165-2-502, 2020.',
    year: 2020,
    tags: ['environmental', 'regulatory', 'wetlands', 'permitting', 'foundational'],
  },
  {
    id: 'env-003',
    topic: 'Air Quality Permitting for Power Generation',
    findings: 'Major-source New Source Review (NSR) applicability turns on a source\'s potential to emit (PTE) after enforceable limits, not on plant MW, and depends on its equipment, fuel, controls and operating duty. Prevention of Significant Deterioration (PSD) applies to a new major source in an attainment area: 100 tons/year of a regulated pollutant for the listed source categories (including fossil fuel-fired steam electric plants above 250 MMBtu/hr heat input) and 250 tons/year otherwise. Nonattainment NSR applies at classification-specific thresholds (lower in serious, severe and extreme areas) and requires the Lowest Achievable Emission Rate (LAER) plus emission offsets. A source below the major-source thresholds can still need a state minor-source or construction permit. No generation technology is exempt by type: a nuclear or solar campus with diesel or gas backup or standby generators still needs air-permit review for those units.',
    source: 'EPA, "New Source Review (NSR) Permitting", 40 CFR Parts 51 and 52, 2023.',
    year: 2023,
    tags: ['environmental', 'regulatory', 'air-quality', 'gas', 'permitting'],
  },

  // Construction & Cost
  {
    id: 'cost-001',
    topic: 'Data Center Construction Costs by Region',
    findings: 'Shell & core: $150-300/sq ft. Mechanical/electrical fit-out: $200-400/sq ft. Total all-in: $8-15M per MW IT capacity. Regional variance: Virginia 1.0x (baseline), Texas 0.9x, California 1.3x, Midwest 0.85x. Labor availability: skilled electricians/HVAC trades critical path item.',
    source: 'JLL, "Data Center Outlook", 2024; Turner & Townsend "International Construction Market Survey", 2024.',
    year: 2024,
    tags: ['datacenter', 'cost', 'construction'],
  },
  {
    id: 'cost-002',
    applicability: 'cross-technology',
    topic: 'Electricity Cost Impact on Data Center Economics',
    findings: 'Electricity is 40-60% of data center operating cost. At $0.05/kWh: $4.4M/yr per 10 MW IT load. At $0.10/kWh: $8.8M/yr. BTM nuclear LCOE target: $0.04-0.06/kWh (nth-of-a-kind). BTM solar+BESS: $0.06-0.10/kWh (location dependent). 10-year electricity cost differential can exceed total construction cost.',
    source: 'Lazard, "Levelized Cost of Energy Analysis, v17.0", 2024.',
    year: 2024,
    tags: ['cost', 'datacenter', 'power', 'btm', 'smr', 'solar', 'general'],
  },

  // Waste Heat Utilization
  {
    id: 'heat-001',
    topic: 'Waste Heat Recovery from Nuclear for District Heating',
    findings: 'SMR thermal output: 200-900 MWth per unit. Waste heat at 80-150°C suitable for: district heating, absorption cooling (COP 0.7-1.2), desalination (MED/MSF), industrial process heat. Combined Heat and Power (CHP) mode increases overall efficiency from 33% to 80%+.',
    source: 'IAEA, "Advances in Small Modular Reactor Technology Developments", 2022 Edition.',
    year: 2022,
    tags: ['smr', 'nuclear', 'cooling', 'heat-recovery', 'efficiency'],
  },

  // Battery Energy Storage
  {
    id: 'bess-001',
    topic: 'Battery Storage for Data Center UPS and Load Smoothing',
    findings: 'Li-ion BESS installed cost: $250-400/kWh (2024). Cycle life: 4,000-8,000 cycles at 80% DoD. Round-trip efficiency: 85-92%. 4-hour duration BESS: $1,000-1,600/kW. Grid-forming inverter capability enables black-start. Replaces traditional diesel UPS for short-duration backup (<4h).',
    source: 'NREL, "2024 Annual Technology Baseline: Utility-Scale Battery Storage", 2024.',
    year: 2024,
    tags: ['bess', 'battery', 'datacenter', 'btm', 'cost', 'resilience'],
  },

  // State Nuclear Policies
  {
    id: 'reg-001',
    topic: 'State-Level Nuclear Energy Policies (US)',
    findings: 'Pro-nuclear states (no moratorium, active incentives): TX, TN, WY, ID, WA, SC, VA, NC. Moratorium states (new plant restrictions): CA, CT, IL, ME, MN, OR, WI. Nuclear-friendly governors/legislation (2023-2024): TX HB 5544, TN SMR Development Act, WY Advanced Nuclear, VA Clean Economy Act.',
    source: 'National Conference of State Legislatures, "State Nuclear Energy Policy", 2024.',
    year: 2024,
    tags: ['regulatory', 'nuclear', 'smr', 'general'],
  },

  // Coastal Zone Permitting
  {
    id: 'coastal-001',
    topic: 'Coastal Zone Industrial Development Permitting',
    findings: 'Federal Coastal Zone Management Act (CZMA) requires consistency review for any federal action in state-designated coastal zones. State coastal commissions (e.g., CCC, NJDEP) add 12-18 months to permitting timelines. Coastal Development Permits required for new industrial construction within coastal zones. Limited precedent for data center or power generation facilities in most state coastal zones — expect heightened scrutiny. Setback requirements typically 100-300 ft from mean high tide line.',
    source: 'NOAA Office for Coastal Management, "Federal Consistency Overview", 2023; 16 U.S.C. §§ 1451-1466.',
    year: 2023,
    tags: ['regulatory', 'coastal'],
  },

  // Decommissioned Nuclear Site Reuse
  {
    id: 'decom-001',
    topic: 'Decommissioned Nuclear Site Reuse for New Energy Projects',
    findings: 'NRC 10 CFR 50.82 governs license termination for decommissioned reactors. ISFSI (Independent Spent Fuel Storage Installation) may remain on-site under separate 10 CFR 72 license, restricting portions of the site. Existing switchyard and transmission infrastructure (typically 230-500 kV) at decommissioned sites retains significant value — grid interconnection already established with utility agreements in place. Brownfield sites offer pre-characterized geology and environmental baselines. NRC supports evaluation of former reactor sites for advanced reactor deployment (SECY-20-0045).',
    source: 'NRC, "Decommissioning Nuclear Power Plants", NUREG-1757 Vol. 1, Rev. 3, 2022; NRC SECY-20-0045.',
    year: 2022,
    tags: ['nuclear', 'decommissioning', 'brownfield', 'grid'],
  },

  // Tsunami & Coastal Flood Risk
  {
    id: 'coastal-002',
    topic: 'Tsunami and Coastal Flood Risk for Critical Facilities',
    findings: 'FEMA coastal flood zones V and VE designate areas subject to velocity wave action (storm surge + breaking waves). ASCE 7-22 Chapter 6 provides tsunami design provisions for Risk Category III/IV structures (including power generation and essential facilities) in tsunami-prone coastlines. NRC Regulatory Guide 1.59 specifies flood design basis for nuclear facilities, requiring Probable Maximum Tsunami (PMT) analysis for coastal sites. Critical facilities in coastal zones should evaluate combined hazards: tsunami, storm surge, sea level rise, and coastal erosion.',
    source: 'FEMA, "Guidelines for Design of Structures for Vertical Evacuation from Tsunamis", P-646, 3rd Ed., 2019; ASCE 7-22 §6.1-6.8; NRC RG 1.59 Rev. 5.',
    year: 2019,
    tags: ['environmental', 'coastal', 'flood'],
  },

  // Military Installation Adjacency
  {
    id: 'mil-001',
    topic: 'Military Installation Compatible Use Zone Requirements',
    findings: 'DoD Instruction 4165.57 requires siting clearance for projects near military installations. Air Installation Compatible Use Zone (AICUZ) studies define noise contours (65-80 dB DNL) and height restriction zones around military airfields. Projects within military influence areas may require DoD review for electromagnetic interference, airspace conflicts, and encroachment concerns. Potential benefit: military installations often have excess utility capacity and established easement corridors that can support adjacent development.',
    source: 'DoD Instruction 4165.57, "Air Installations Compatible Use Zones (AICUZ)", 2019; 10 U.S.C. § 2684a.',
    year: 2019,
    tags: ['regulatory', 'military', 'site'],
  },

  // State Nuclear Moratorium Policies
  {
    id: 'reg-002',
    topic: 'State Nuclear Construction Moratorium Implications for SMR Deployment',
    findings: 'Several US states maintain moratoriums on new nuclear power plant construction, typically conditioned on federal demonstration of a permanent waste repository. Federal preemption under the Atomic Energy Act (42 U.S.C. § 2011) means NRC retains exclusive authority over radiological safety and reactor licensing — state moratoriums are based on economic/waste concerns, not safety jurisdiction. SMR developers in moratorium states face dual challenge: state legislative action required plus standard NRC licensing. Recent legislative activity (2023-2024) in several moratorium states to revisit or repeal restrictions due to clean energy mandates and data center power demand.',
    source: 'Congressional Research Service, "Nuclear Energy: Overview of Congressional Issues", R42853, 2023; Pacific Legal Foundation v. State Energy Resources, 461 U.S. 190 (1983).',
    year: 2023,
    tags: ['regulatory', 'nuclear'],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 10 CFR PART 52 — NUCLEAR SITING REQUIREMENTS
  // ═══════════════════════════════════════════════════════════════════════════

  // ESP / COL Process
  {
    id: 'cfr52-001',
    topic: '10 CFR Part 52 Licensing Pathways: ESP, COL, Standard Design',
    findings: '10 CFR Part 52 provides three licensing pathways: (1) Early Site Permit (ESP, Subpart A) — approves site for up to 20 years, independent of reactor design, evaluates site characteristics including seismology, meteorology, geology, hydrology, and population distribution; (2) Combined License (COL, Subpart C) — single license for construction AND operation, requires emergency planning, FSAR, environmental review per 10 CFR 51; (3) Standard Design Certification (Subpart B) — pre-approved reactor designs referenced in COL applications. ESP+COL pathway reduces total licensing from 8-12 years to 5-7 years by resolving site suitability early. NRC 10 CFR 52.17 requires ESP applicant to demonstrate no significant hazard to population health and safety.',
    source: 'Title 10 CFR Part 52 "Licenses, Certifications, and Approvals for Nuclear Power Plants"; NRC NUREG-0800 Standard Review Plan.',
    year: 2024,
    tags: ['nuclear', 'regulatory', 'smr', 'foundational'],
  },
  {
    id: 'cfr52-002',
    topic: 'ESP Site Characteristics: Geological and Seismological Assessment (10 CFR 100.23)',
    findings: 'ESP applicants must evaluate: (a) Safe Shutdown Earthquake (SSE) ground motion — site-specific PSHA required for PGA > 0.1g or SDC ≥ C; (b) Surface deformation potential from capable faults within 200 miles (320 km); (c) Soil stability including liquefaction potential for saturated sandy soils; (d) Foundation conditions (rock vs soil bearing capacity); (e) Slope stability under seismic and static loads. Per NRC RG 1.208, geotechnical investigation must include borings to 200 ft depth, shear wave velocity profile, and dynamic soil properties. For SMRs, seismic isolation systems can reduce building design from SDC D to equivalent SDC B performance.',
    source: '10 CFR 100.23 "Geologic and Seismic Siting Criteria"; NRC Regulatory Guide 1.208 "Performance-Based Approach for PSHA"; ASCE 4-16 "Seismic Analysis of Nuclear Structures".',
    year: 2024,
    tags: ['nuclear', 'seismic', 'site'],
  },
  {
    id: 'cfr52-003',
    topic: 'Emergency Planning Zones: Traditional vs. SMR Reduced EPZ',
    findings: 'Traditional reactors (>1000 MWt): Plume Exposure Pathway EPZ = 10-mile radius, Ingestion Pathway EPZ = 50-mile radius. Requires off-site emergency planning with state/local government, evacuation time estimates, sirens, KI distribution, and annual exercises. NRC SECY-18-0103 allows SMR/advanced reactor applicants to use source-term-based EPZ sizing — for passively safe SMRs with low source terms, EPZ can be reduced to site boundary (~0.5-2 km). This eliminates off-site emergency planning requirements, significantly reducing cost and regulatory complexity. Population within EPZ is a critical siting factor: NRC prefers sites where EAB can be controlled and LPZ population is low. Per 10 CFR 50.34(a)(1), EAB is the area within which the reactor licensee has exclusive authority to control activities.',
    source: 'NRC SECY-18-0103 "Emergency Planning for SMRs and Other New Technologies"; 10 CFR 50 Appendix E "Emergency Plans"; NUREG-0654 "Emergency Planning Criteria".',
    year: 2024,
    tags: ['nuclear', 'regulatory', 'smr', 'site', 'foundational'],
    reactorClasses: ['lwr-smr', 'htgr', 'molten-salt', 'microreactor', 'large-lwr'],
  },
  {
    id: 'cfr52-004',
    topic: 'Environmental Impact Statement Requirements (10 CFR Part 51, NEPA)',
    findings: 'NRC environmental review under NEPA/10 CFR 51 for ESP or COL requires comprehensive EIS covering: (1) Land use — site acreage, zoning compatibility, exclusion zone buffer; (2) Water resources — cooling water source, thermal discharge limits (316(b) CWA), groundwater impacts, 100-yr flood plain; (3) Terrestrial ecology — critical habitat (ESA Section 7 consultation, 3-6 months), wetlands (Section 404 CWA, 6-12 months mitigation plan), threatened/endangered species; (4) Aquatic ecology — intake entrainment/impingement study; (5) Air quality — construction dust, cooling tower drift, NOx/SOx from backup generators; (6) Socioeconomics — workforce housing, traffic, property values; (7) Cultural resources — Section 106 NHPA consultation; (8) Radiological impacts — occupational dose (<5 rem/yr), public dose (<100 mrem/yr design basis, <2 mrem/yr design objective per 10 CFR 20). Typical EIS review: 18-30 months. Draft EIS published for 45-day public comment per 40 CFR 1503.',
    source: '10 CFR 51 "Environmental Protection Regulations for Domestic Licensing"; NUREG-1555 "Environmental Standard Review Plan"; 40 CFR 1500-1508 "CEQ NEPA Regulations".',
    year: 2024,
    tags: ['nuclear', 'environmental', 'regulatory', 'site', 'foundational'],
  },
  {
    id: 'cfr52-005',
    topic: 'Site Exclusion Area Boundary and Low Population Zone Requirements',
    findings: 'Per 10 CFR 100.11: (1) Exclusion Area Boundary (EAB) — the applicant must have exclusive authority to control activities within this zone. Minimum size determined by dose calculation: 25 rem TEDE whole body or 300 rem thyroid at EAB during 2-hour design basis accident. Traditional large reactors: EAB typically 0.5-1 mile. SMRs with low source terms: EAB reduced to plant fence line (~0.1-0.3 km). (2) Low Population Zone (LPZ) — zone beyond EAB where public dose during accident < 25 rem TEDE. NRC guidance favors sites where LPZ population is sufficiently low to enable evacuation or sheltering. (3) Population center distance — nearest city > 25,000 people should be at least 1.33× LPZ radius from reactor. These criteria directly affect site selection: rural sites with large available land and low population density are strongly preferred.',
    source: '10 CFR 100.11 "Determination of Exclusion Area, Low Population Zone, and Population Center Distance"; NRC RG 4.7 "General Site Suitability Criteria for Nuclear Power Stations".',
    year: 2024,
    tags: ['nuclear', 'site', 'regulatory', 'foundational'],
  },
  {
    id: 'cfr52-006',
    topic: 'Meteorological Assessment for Nuclear Site Characterization',
    findings: 'NRC RG 1.23 requires minimum 1 year of on-site meteorological data at multiple levels (10m, 60m, facility height): wind speed/direction, temperature differential (stability), precipitation, humidity. Data used for: (1) Atmospheric dispersion modeling (NRC RG 1.145, Gaussian plume: χ/Q = f(σy, σz, wind, stability)); (2) Design basis tornado wind speed (ASCE 7-22 tornado region map); (3) Cooling system design (wet bulb, dry bulb, humidity profiles); (4) Probable Maximum Precipitation (PMP) for flood analysis. Pre-construction screening can use NOAA/ERA5 reanalysis data (30-year normals) for feasibility, but NRC requires site-specific tower data for formal licensing. Key screening parameters: annual average wind speed > 2 m/s (adequate dispersion), low frequency of inversions, no persistent fog or valley wind channeling.',
    source: 'NRC Regulatory Guide 1.23 "Meteorological Monitoring Programs for Nuclear Power Plants"; NRC RG 1.145 "Atmospheric Dispersion Models for Design Basis Accident Analysis".',
    year: 2024,
    tags: ['nuclear', 'cooling', 'site', 'climate'],
  },
  {
    id: 'cfr52-007',
    topic: 'Hydrological Assessment: Cooling Water and Liquid Effluent Pathways',
    findings: 'Nuclear siting requires assessment of: (1) Cooling water source adequacy — once-through systems need 25,000-60,000 gal/MWh flow from river/lake; closed-loop towers need 500-800 gal/MWh makeup; dry cooling eliminates water but 5-7% efficiency penalty. (2) Probable Maximum Flood (PMF) — site grade must be above PMF elevation or protected by safety-grade flood barriers. (3) Groundwater — baseline monitoring wells required pre-construction; tritium/fission product transport modeling for accident scenarios. (4) Thermal discharge — Section 316(a) CWA thermal discharge limits (varies by state, typically ΔT < 3°C above ambient for riverine, < 1.5°C for estuarine). (5) Water rights — prior appropriation (western states) vs. riparian rights (eastern states); nuclear cooling typically classified as consumptive use requiring state water permit (3-18 month timeline). SMR advantage: passive cooling designs (NuScale, Xe-100) have minimal water requirements, enabling sites without large water bodies.',
    source: '10 CFR 50 Appendix A GDC 44 "Cooling Water"; NRC RG 1.113 "Estimating Aquatic Biota Dose"; Clean Water Act Section 316(a)(b).',
    year: 2024,
    tags: ['nuclear', 'cooling', 'environmental', 'site'],
  },
  {
    id: 'cfr52-008',
    topic: 'External Hazards Assessment for Nuclear Siting',
    findings: 'Per 10 CFR 100 and NRC RG 1.91, nuclear sites must be evaluated for external hazards: (1) Aircraft crash — sites within 5 miles of airport runway or military flight corridor require probabilistic crash risk analysis; <1E-7/yr acceptable. (2) Industrial hazards — chemical plants, refineries, pipelines, and gas storage within 5 miles; vapor cloud explosion/toxic release analysis per RG 1.91. (3) Transportation hazards — highway/rail hazmat cargo frequency within 1 mile; LPG/LNG transport risk. (4) Dam failure — sites downstream of dams require PMF + dam breach flood analysis per NRC JLD-ISG-2013-01. (5) Military facilities — DoD AICUZ zones, weapons testing ranges, ordnance storage within blast radius. (6) Wildfire — vegetation management within EAB; NFPA 805 fire protection. (7) Volcanic hazards — sites within 200 miles of Holocene volcano require screening per RG 4.7. Screening distances: 5-mile chemical/industrial, 10-mile airport, 50-mile dam failure, 200-mile volcano.',
    source: 'NRC Regulatory Guide 1.91 "Evaluations of Explosions Postulated to Occur at Nearby Facilities"; 10 CFR 100.20-21 "Factors for Site Evaluation"; NRC JLD-ISG-2013-01 (dam failure flood).',
    year: 2024,
    tags: ['nuclear', 'site', 'environmental', 'flood'],
  },
  {
    id: 'cfr52-009',
    topic: 'NRC Decommissioning and Financial Qualifications',
    findings: 'Per 10 CFR 50.75, reactor licensees must provide financial assurance for decommissioning before construction. Minimum decommissioning fund: $500M-1B for large reactors (1000+ MWe), $50-200M for SMRs (scaled by power and complexity). Funding mechanisms: prepayment, external sinking fund (2-3% annual set-aside), surety bond, or parent company guarantee. Decommissioning timeline: 10-60 years (DECON vs. SAFSTOR options). License termination per 10 CFR 20.1402 requires <25 mrem/yr residual dose. ISFSI (Independent Spent Fuel Storage Installation) may remain on site 40+ years under 10 CFR 72 — reduces available buildable area by 2-5 acres.',
    source: '10 CFR 50.75 "Reporting and Recordkeeping for Decommissioning Planning"; 10 CFR 50.82 "Termination of License"; NUREG-1757 "Consolidated Decommissioning Guidance".',
    year: 2024,
    tags: ['nuclear', 'regulatory'],
  },
  {
    id: 'iaea-001',
    topic: 'IAEA Nuclear Siting Safety Guide: Site Evaluation Framework',
    findings: 'IAEA Safety Standards Series SSG-9 (Rev.1) establishes international nuclear siting framework: (1) Exclusion criteria — sites disqualified if: capable fault crosses site, volcanic hazard within 10km, site below PMF elevation with no protection, population density > 500/km² within 5km. (2) Discretionary criteria — sites evaluated for: seismic hazard (PSHA), meteorological dispersion, hydrology, population distribution, emergency planning capability, grid connection stability, transportation access. (3) Site investigation stages: Stage 1 (regional survey, 300km radius), Stage 2 (site screening, 50km radius), Stage 3 (candidate site evaluation, 5km radius), Stage 4 (detailed site characterization). IAEA GSR Part 1 requires "defense in depth" — site characteristics should not challenge safety functions (cooling, containment, control). Key insight for SMR siting: IAEA recognizes technology-informed site requirements — passively safe designs may relax population distance criteria.',
    source: 'IAEA SSG-9 (Rev.1) "Site Evaluation for Nuclear Installations"; IAEA GSR Part 1 "Governmental, Legal and Regulatory Framework for Safety"; IAEA NS-R-3 (Rev.1) "Site Evaluation for Nuclear Installations".',
    year: 2022,
    tags: ['nuclear', 'site', 'regulatory', 'seismic', 'environmental'],
  },
];

/**
 * Retrieve relevant knowledge entries for a report context.
 *
 * Scored tag-match (Existing Library Ingest & Usage Audit Prompt 6,
 * superseding the binary filter + Content-Source Prompt 12's
 * cross-cutting-tag top-up):
 *
 *   score = 0.6 · (|entry.tags ∩ siteTags| / max(|siteTags|, 1))
 *         + 0.2 · (entry.tags has 'general')
 *         + 0.2 · (entry.tags has 'foundational')
 *
 * "Matched" entries are those with a *real* tag intersection — the
 * 0.2 'general'/'foundational' boosts only re-rank within the matched
 * set and pick the top-up, so callers that filter by a specific tag
 * still get only entries that actually carry that tag. When fewer than
 * `minResults` entries are matched (unusual workloads — hydrogen-coloc,
 * district-heating, etc.), the slice is topped up with foundational /
 * general / *-general entries so a real KB anchor still reaches the LLM
 * instead of inviting it to invent citations. A dev-only warning fires
 * when the *matched-by-tag* set is < 5 so we notice the KB needs more
 * coverage for that workload.
 */
export const MIN_KNOWLEDGE_ENTRIES = 8;
const KNOWLEDGE_WARN_THRESHOLD = 5;
/** Tags that mark an entry as broadly applicable enough to top-up a
 *  thin slice with. 'general' / 'foundational' are the canonical ones;
 *  the '*-general' variants are accepted for forward-compat with a
 *  future per-domain general-axis tagging pass. */
const TOPUP_TAGS = new Set(['general', 'foundational', 'data-center-general', 'regulatory-general', 'seismic-general', 'environmental-general']);

interface ScoredEntry { entry: KnowledgeEntry; overlap: number; score: number; }

/** Stable comparator: relevance score desc, then recency desc, then id
 *  asc — deterministic across runs regardless of array order. */
function byScoreThenRecencyThenId(a: ScoredEntry, b: ScoredEntry): number {
  return (b.score - a.score) || (b.entry.year - a.entry.year) || a.entry.id.localeCompare(b.entry.id);
}

/* ── Curated overlay (Evidence Auto-Curator EC1) ─────────────────────
 *
 * The static `PEER_REVIEW_DB` above is the baseline. Entries an admin
 * approves in the AdminPanel → Library Curator tab are appended to a
 * Firestore `curated_kb_entries` collection (Cloud Run is immutable, so
 * we can't edit the source file at runtime). `appendCuratedKnowledgeEntry`
 * keeps an in-memory mirror so the entry shows up in the *same* session
 * after approval; `hydrateCuratedKnowledge` re-loads the collection on
 * sign-in (called from AuthGate, best-effort). `getRelevantKnowledge`
 * scores over `PEER_REVIEW_DB ∪ curatedEntries`.
 */
let _curatedEntries: KnowledgeEntry[] = [];

/** All KB entries the matcher considers — baseline + curated overlay,
 *  de-duplicated by `id` (a curated entry never shadows a baseline one). */
export function getAllKnowledgeEntries(): KnowledgeEntry[] {
  if (_curatedEntries.length === 0) return PEER_REVIEW_DB;
  const seen = new Set(PEER_REVIEW_DB.map(e => e.id));
  const extra = _curatedEntries.filter(e => !seen.has(e.id));
  return extra.length === 0 ? PEER_REVIEW_DB : [...PEER_REVIEW_DB, ...extra];
}

/** Append a curated entry to the in-memory overlay (idempotent by id).
 *  The AdminPanel also writes it to Firestore so it survives a reload —
 *  this just makes it visible immediately. */
export function appendCuratedKnowledgeEntry(entry: KnowledgeEntry): void {
  if (PEER_REVIEW_DB.some(e => e.id === entry.id)) return;
  const i = _curatedEntries.findIndex(e => e.id === entry.id);
  if (i >= 0) _curatedEntries[i] = entry;
  else _curatedEntries.push(entry);
}

/** Replace the in-memory overlay wholesale (used by the hydrate path). */
export function setCuratedKnowledgeEntries(entries: KnowledgeEntry[]): void {
  _curatedEntries = entries.filter(e => !PEER_REVIEW_DB.some(b => b.id === e.id));
}

/** Best-effort: load the `curated_kb_entries` Firestore collection into
 *  the overlay. Called by AuthGate after sign-in; on failure (offline,
 *  permission-denied for non-admins — which is fine, the collection is
 *  readable by any authed user) the baseline stays. */
export async function hydrateCuratedKnowledge(): Promise<void> {
  try {
    const { fetchCuratedKnowledgeEntries } = await import('./curatedKnowledgeStore');
    setCuratedKnowledgeEntries(await fetchCuratedKnowledgeEntries());
  } catch (e) {
    console.warn('[peerReviewDb] hydrateCuratedKnowledge failed; keeping baseline KB only:', e);
  }
}

/** Also accepts immutable SED rows whose older schema omitted applicability tags. */
export function filterKnowledgeForIntent<T extends { id: string; topic: string; tags?: string[]; applicability?: string }>(entries: T[], nuclearInterest?: string): T[] {
  if (nuclearInterest !== 'no') return entries;
  const known = new Map(getAllKnowledgeEntries().map(entry => [entry.id, entry]));
  return entries.filter(entry => {
    const metadata = entry.tags ? entry : known.get(entry.id);
    if (metadata?.applicability === 'cross-technology') return true;
    if (metadata?.tags?.some(tag => ['nuclear', 'smr'].includes(tag.toLowerCase()))) return false;
    // Unknown legacy/curated entries lack tags: a reactor-specific title is
    // enough to withhold that guidance, never to infer a new project intent.
    return !/\b(?:nuclear|SMRs?|reactors?|NRC|IAEA)\b|10\s*CFR\s*(?:Part\s*)?(?:50|51|52|100)\b/i.test(entry.topic);
  });
}

export function getRelevantKnowledge(siteTags: string[], opts?: { minResults?: number; nuclearInterest?: string }): KnowledgeEntry[] {
  const minResults = opts?.minResults ?? MIN_KNOWLEDGE_ENTRIES;
  const tagSet = new Set(siteTags.map(t => t.toLowerCase()));
  const denom = Math.max(tagSet.size, 1);
  // Apply explicit technology exclusions before scoring AND top-up. Shared
  // environmental/regulatory tags must not reintroduce NRC reactor licensing
  // into a project whose owner has ruled out nuclear generation.
  const corpus = filterKnowledgeForIntent(getAllKnowledgeEntries(), opts?.nuclearInterest);

  const scored: ScoredEntry[] = corpus.map(entry => {
    const overlap = entry.tags.filter(t => tagSet.has(t.toLowerCase())).length;
    const score =
      0.6 * (overlap / denom)
      + 0.2 * (entry.tags.includes('general') ? 1 : 0)
      + 0.2 * (entry.tags.includes('foundational') ? 1 : 0);
    return { entry, overlap, score };
  });

  const matchedScored = scored.filter(s => s.overlap > 0).sort(byScoreThenRecencyThenId);
  const matched = matchedScored.map(s => s.entry);

  if (matched.length < KNOWLEDGE_WARN_THRESHOLD) {
    console.warn(`[peerReviewDb] only ${matched.length} KB ${matched.length === 1 ? 'entry' : 'entries'} tag-matched siteTags=[${siteTags.join(', ')}] (< ${KNOWLEDGE_WARN_THRESHOLD}) — topping up from foundational/general entries; consider curating more KB coverage for this workload.`);
  }

  if (matched.length >= minResults) return matched;

  // Top-up: foundational / general / *-general entries not already
  // matched, highest-relevance first.
  const matchedIds = new Set(matched.map(e => e.id));
  const topUp = scored
    .filter(s => !matchedIds.has(s.entry.id) && s.entry.tags.some(t => TOPUP_TAGS.has(t)))
    .sort(byScoreThenRecencyThenId)
    .slice(0, minResults - matched.length)
    .map(s => s.entry);

  let result = [...matched, ...topUp];

  // Still short — the KB doesn't yet carry enough foundational/general
  // entries. Pad from the remaining entries by recency so the slice
  // still beats a near-empty one (the dev warning above already
  // flagged the under-coverage).
  if (result.length < minResults) {
    const have = new Set(result.map(e => e.id));
    const pad = [...corpus]
      .filter(e => !have.has(e.id))
      .sort((a, b) => (b.year - a.year) || a.id.localeCompare(b.id))
      .slice(0, minResults - result.length);
    result = [...result, ...pad];
  }

  return result;
}

/**
 * Format knowledge entries as numbered references for Gemini prompts.
 * Each entry gets a [Ref N] number that Gemini should cite inline.
 */
export function formatKnowledgeContext(entries: KnowledgeEntry[], startIndex: number = 1): string {
  if (entries.length === 0) return '';
  return '\n\nNUMBERED REFERENCE LIBRARY (cite as [Ref N] in your text — these appear in the report appendix):\n' +
    entries.map((e, i) => `[Ref ${startIndex + i}] ${e.topic}: ${e.findings} [Source: ${e.source}]`).join('\n') +
    '\n\nIMPORTANT: When you use information from a reference, cite it inline as [Ref N]. For example: "SMR reactors require 0.5-2 acres per 100 MW [Ref 1]." This links your text to the appendix.';
}
